import { useQuery } from "@tanstack/react-query";
import { api, eur } from "@/lib/api";
import { PLANS } from "@/lib/plans";
import { Building2, BadgeCheck, Wallet, CalendarDays, Loader2, Users, ShoppingCart, AlertTriangle } from "lucide-react";
import { t } from "@/lib/i18n";

function Kpi({ icon: Icon, label, value, sub, testid, dark }) {
  return (
    <div className={`rounded-2xl p-5 border ${dark ? "bg-slate-950 text-white border-slate-800" : "bg-white border-border"}`} data-testid={testid}>
      <div className="flex items-center justify-between">
        <span className={`text-[11px] font-mono-j uppercase tracking-wider ${dark ? "text-slate-400" : "text-muted-foreground"}`}>{label}</span>
        <Icon className={`h-4 w-4 ${dark ? "text-emerald-400" : "text-primary"}`} />
      </div>
      <p className="font-display text-3xl font-extrabold mt-3">{value}</p>
      {sub && <p className={`text-xs mt-1 ${dark ? "text-slate-400" : "text-muted-foreground"}`}>{sub}</p>}
    </div>
  );
}

const fmtDate = (s) => (s ? new Date(s).toLocaleDateString("it-IT") : "—");

export function AdminOverview() {
  const { data: o, isLoading } = useQuery({
    queryKey: ["admin-overview"],
    queryFn: async () => (await api.get("/admin/overview")).data,
  });
  if (isLoading) return <div className="grid place-items-center h-60 text-muted-foreground gap-2"><Loader2 className="h-6 w-6 animate-spin text-primary" />{t("Lettura pagamenti da PayPal…")}</div>;
  return (
    <div className="space-y-6" data-testid="admin-overview">
      <div>
        <h1 className="font-display text-3xl font-extrabold tracking-tight">{t("Panoramica")}</h1>
        <p className="text-sm text-muted-foreground">{t("Incassi letti in tempo reale da PayPal")}</p>
      </div>
      {!o?.paypal_ok && <p className="flex items-center gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3" data-testid="admin-paypal-warning"><AlertTriangle className="h-4 w-4" /> {t("Impossibile leggere i pagamenti da PayPal in questo momento.")}</p>}
      <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <Kpi icon={Building2} label={t("Aziende totali")} value={o?.companies_total ?? 0} sub={`${o?.trialing ?? 0} in prova · ${o?.expired ?? 0} scadute`} testid="admin-kpi-companies" />
        <Kpi icon={BadgeCheck} label={t("Abbonamenti attivi")} value={o?.active_subscriptions ?? 0} testid="admin-kpi-active" />
        <Kpi dark icon={Wallet} label={t("Incassi totali")} value={eur(o?.revenue_total)} testid="admin-kpi-revenue-total" />
        <Kpi dark icon={CalendarDays} label={t("Incassi del mese")} value={eur(o?.revenue_month)} testid="admin-kpi-revenue-month" />
      </div>
      <div className="grid lg:grid-cols-3 gap-4">
        <div className="bg-white border border-border rounded-2xl p-5 space-y-3" data-testid="admin-plans-breakdown">
          <h3 className="font-semibold">{t("Abbonamenti per piano")}</h3>
          {PLANS.map((p) => (
            <div key={p.key} className="flex items-center justify-between text-sm">
              <span className="flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full" style={{ background: p.color }} />{p.name} · {p.price}€</span>
              <span className="font-semibold" data-testid={`admin-plan-count-${p.key}`}>{o?.by_plan?.[p.key] ?? 0}</span>
            </div>
          ))}
          <div className="border-t border-border pt-3 flex justify-between text-sm text-muted-foreground"><span className="flex items-center gap-2"><Users className="h-3.5 w-3.5" />{t("Agenti")}</span><span>{o?.agents_total ?? 0}</span></div>
          <div className="flex justify-between text-sm text-muted-foreground"><span className="flex items-center gap-2"><ShoppingCart className="h-3.5 w-3.5" />{t("Ordini")}</span><span>{o?.orders_total ?? 0}</span></div>
        </div>
        <div className="lg:col-span-2 bg-white border border-border rounded-2xl p-5" data-testid="admin-recent-payments">
          <h3 className="font-semibold mb-3">{t("Ultimi pagamenti")}</h3>
          {o?.recent_payments?.length ? o.recent_payments.map((p) => (
            <div key={p.id} className="flex justify-between text-sm border-b border-border/60 py-2 last:border-0">
              <span>{p.company_name}<span className="text-xs text-muted-foreground ml-2">{fmtDate(p.date)}</span></span>
              <span className="font-semibold">{eur(p.amount)}</span>
            </div>
          )) : <p className="text-sm text-muted-foreground">{t("Nessun pagamento ricevuto finora.")}</p>}
        </div>
      </div>
    </div>
  );
}
